import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Heart, Users, Leaf, Palette, Briefcase, Building2, MapPin,
  Search, Filter, X, Loader2, ChevronRight, Star, Plus,
  Gift, DollarSign, Calendar, Handshake, CheckCircle, ArrowLeft
} from 'lucide-react';
import { useLocation } from '../context/LocationContext';
import Breadcrumbs from '../components/Breadcrumbs';
import useTenantGeoRedirect from '../hooks/useTenantGeoRedirect';
// Use centralized API service
import { municipiosApi, socialApi } from '../services/api';
import { resolveBackendImage } from '../utils/brandLogo';

// Area labels
const AREA_LABELS = {
  'assistencia_social': { label: 'Assistência Social', icon: '🤝', color: 'bg-pink-100 text-pink-700' },
  'cultura': { label: 'Cultura', icon: '🎭', color: 'bg-purple-100 text-purple-700' },
  'esporte': { label: 'Esporte', icon: '⚽', color: 'bg-green-100 text-green-700' },
  'educacao': { label: 'Educação', icon: '📚', color: 'bg-blue-100 text-blue-700' },
  'saude': { label: 'Saúde', icon: '🏥', color: 'bg-red-100 text-red-700' },
  'meio_ambiente': { label: 'Meio Ambiente', icon: '🌱', color: 'bg-emerald-100 text-emerald-700' },
  'causa_animal': { label: 'Causa Animal', icon: '🐾', color: 'bg-amber-100 text-amber-700' },
  'direitos_humanos': { label: 'Direitos Humanos', icon: '✊', color: 'bg-orange-100 text-orange-700' },
  'empreendedorismo_social': { label: 'Empreendedorismo', icon: '💡', color: 'bg-yellow-100 text-yellow-700' },
  'outros': { label: 'Outros', icon: '📌', color: 'bg-gray-100 text-gray-700' }
};

// Entity type labels
const ENTITY_LABELS = {
  'ong': 'ONG',
  'associacao': 'Associação',
  'coletivo': 'Coletivo',
  'projeto_independente': 'Projeto',
  'projeto_pessoa_fisica': 'Projeto PF',
  'osc': 'OSC',
  'oscip': 'OSCIP',
  'fundacao': 'Fundação',
  'instituto': 'Instituto',
  'outro': 'Outro'
};

// Social Impact Badge Component
const ImpactBadge = ({ icon: Icon, label, color }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
    <Icon className="w-3 h-3" />
    {label}
  </span>
);

// Organization Card Component
const OrgCard = ({ org, onClick }) => {
  const isVerified = org.verification_status !== 'not_verified';
  const supportOptions = org.support_options || {};
  
  // Get CTAs
  const ctas = [];
  if (supportOptions.donate_items?.enabled) ctas.push({ icon: Gift, label: 'Doações', color: 'text-orange-500' });
  if (supportOptions.donate_money?.enabled) ctas.push({ icon: DollarSign, label: 'Doar $', color: 'text-green-500' });
  if (supportOptions.volunteer?.enabled) ctas.push({ icon: Users, label: 'Voluntário', color: 'text-blue-500' });
  if (supportOptions.participate?.enabled) ctas.push({ icon: Calendar, label: 'Participar', color: 'text-purple-500' });
  if (supportOptions.partnership?.enabled) ctas.push({ icon: Handshake, label: 'Parceria', color: 'text-teal-500' });

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
      data-testid={`org-card-${org.id}`}
    >
      <div className="flex gap-4">
        {/* Avatar */}
        <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl overflow-hidden flex-shrink-0">
          {org.avatar_url ? (
            <img 
              src={resolveBackendImage(org.avatar_url)} 
              alt={org.name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Heart className="w-8 h-8 text-purple-400" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900 truncate">{org.name}</h3>
                {isVerified && (
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                )}
              </div>
              <p className="text-xs text-gray-500">
                {ENTITY_LABELS[org.entity_type] || org.entity_type}
              </p>
              {org.neighborhood && (
                <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" />
                  {org.neighborhood}
                </p>
              )}
            </div>
          </div>

          {org.short_description && (
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">
              {org.short_description}
            </p>
          )}

          {/* Areas */}
          <div className="flex flex-wrap gap-1 mt-2">
            {(org.areas_of_action || []).slice(0, 3).map(area => {
              const areaInfo = AREA_LABELS[area] || { label: area, icon: '📌', color: 'bg-gray-100 text-gray-700' };
              return (
                <span 
                  key={area}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${areaInfo.color}`}
                >
                  {areaInfo.icon} {areaInfo.label}
                </span>
              );
            })}
          </div>

          {/* CTAs */}
          {ctas.length > 0 && (
            <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-100">
              {ctas.slice(0, 4).map((cta, idx) => (
                <span key={idx} className={`flex items-center gap-1 text-xs ${cta.color}`}>
                  <cta.icon className="w-3.5 h-3.5" />
                  {cta.label}
                </span>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            {(org.hearts_count > 0) && (
              <span
                className="flex items-center gap-1 text-pink-600 font-semibold"
                data-testid={`org-card-hearts-${org.id}`}
                title={`${org.hearts_count} depoimento${org.hearts_count > 1 ? 's' : ''}`}
              >
                <Heart className="w-3.5 h-3.5 fill-pink-500 text-pink-500" />
                {(org.hearts_avg || 0).toFixed(1)}
                <span className="text-gray-400 font-normal">({org.hearts_count})</span>
              </span>
            )}
            {org.supporters_count > 0 && (
              <span>{org.supporters_count} apoiadores</span>
            )}
            {org.active_activities_count > 0 && (
              <span>{org.active_activities_count} ações ativas</span>
            )}
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 self-center" />
      </div>
    </div>
  );
};

// Filter Chip Component
const FilterChip = ({ label, icon: Icon, active, onClick, color }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
      active
        ? `${color} text-white`
        : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-300'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

export default function Social() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const { location } = useLocation();
  
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [municipio, setMunicipio] = useState(null);

  // Auto-redirect /social → /<slug>/social respecting user's chosen município
  useTenantGeoRedirect({ routeName: 'social', currentSlug: municipioSlug });
  const [activeFilters, setActiveFilters] = useState({
    area: '',
    verified: false,
    support_type: ''
  });

  // Load municipality info
  useEffect(() => {
    const loadMunicipio = async () => {
      if (municipioSlug) {
        try {
          const allMunicipios = await municipiosApi.list();
          const found = allMunicipios.find(m => m.slug === municipioSlug);
          if (found) {
            setMunicipio(found);
          }
        } catch (error) {
          console.error('Erro ao carregar município:', error);
        }
      }
    };
    loadMunicipio();
  }, [municipioSlug]);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.area) params.area = activeFilters.area;
      if (activeFilters.verified) params.verified = 'true';
      if (activeFilters.support_type) params.support_type = activeFilters.support_type;
      if (searchQuery) params.search = searchQuery;
      if (municipioSlug) params.municipio_slug = municipioSlug;
      
      const data = await socialApi.list(params);
      setOrgs(data);
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
    } finally {
      setLoading(false);
    }
  }, [activeFilters, searchQuery, municipioSlug]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  const setAreaFilter = (area) => {
    setActiveFilters(prev => ({ 
      ...prev, 
      area: prev.area === area ? '' : area 
    }));
  };

  const setSupportFilter = (support) => {
    setActiveFilters(prev => ({ 
      ...prev, 
      support_type: prev.support_type === support ? '' : support 
    }));
  };

  const areaFilters = [
    { key: 'assistencia_social', label: 'Assistência', icon: Heart, color: 'bg-pink-500' },
    { key: 'cultura', label: 'Cultura', icon: Palette, color: 'bg-purple-500' },
    { key: 'educacao', label: 'Educação', icon: Users, color: 'bg-blue-500' },
    { key: 'meio_ambiente', label: 'Meio Ambiente', icon: Leaf, color: 'bg-green-500' },
  ];

  const supportFilters = [
    { key: 'donate_items', label: 'Doações', icon: Gift, color: 'bg-orange-500' },
    { key: 'volunteer', label: 'Voluntário', icon: Users, color: 'bg-blue-500' },
    { key: 'participate', label: 'Participar', icon: Calendar, color: 'bg-purple-500' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Breadcrumbs */}
          <Breadcrumbs 
            items={[
              { label: 'Impacto Social', path: null }
            ]}
            municipio={municipio}
            className="mb-4"
          />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8" />
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Impacto Social
              </h1>
            </div>
            <button
              onClick={() => navigate('/social/cadastrar-organizacao')}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full font-medium transition-all"
              data-testid="add-org-button"
            >
              <Plus className="w-5 h-5" />
              Cadastrar
            </button>
          </div>
          <p className="text-purple-100">
            ONGs, associações, coletivos e projetos que fazem a diferença
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nome, descrição ou bairro..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              data-testid="social-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>

          {/* Area Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="text-sm text-gray-500 self-center">Área:</span>
            {areaFilters.map(f => (
              <FilterChip
                key={f.key}
                label={f.label}
                icon={f.icon}
                color={f.color}
                active={activeFilters.area === f.key}
                onClick={() => setAreaFilter(f.key)}
              />
            ))}
          </div>

          {/* Support Filters */}
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="text-sm text-gray-500 self-center">Apoiar:</span>
            {supportFilters.map(f => (
              <FilterChip
                key={f.key}
                label={f.label}
                icon={f.icon}
                color={f.color}
                active={activeFilters.support_type === f.key}
                onClick={() => setSupportFilter(f.key)}
              />
            ))}
            <button
              onClick={() => setActiveFilters(prev => ({ ...prev, verified: !prev.verified }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeFilters.verified
                  ? 'bg-blue-500 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'
              }`}
            >
              <CheckCircle className="w-4 h-4" />
              Verificados
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-600">
            <span className="font-bold text-purple-600">{orgs.length}</span>
            {' '}{orgs.length === 1 ? 'organização encontrada' : 'organizações encontradas'}
          </p>
        </div>

        {/* Organizations List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : orgs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-700 mb-2">
              {searchQuery || activeFilters.area || activeFilters.support_type || activeFilters.verified
                ? 'Nenhuma organização encontrada'
                : 'Nenhuma organização cadastrada'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Tente buscar por outros termos'
                : 'Seja o primeiro a cadastrar uma organização!'}
            </p>
            <button
              onClick={() => navigate('/social/cadastrar-organizacao')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700"
            >
              <Plus className="w-5 h-5" />
              Cadastrar Organização
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orgs.map(org => (
              <OrgCard
                key={org.id}
                org={org}
                onClick={() => navigate(`/social/${org.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
